<template>
  <div class="collection-card-wrapper">
    <div class="collection-header">
      <div>
        <h4 class="collection-title">{{ collection.collectionName }}</h4>

        <div class="collection-details">
          <span>{{ numberOfDocuments }}</span>
          <span class="bullet">•</span>
          <span>{{ formatUpdatedAt }}</span>
        </div>
      </div>

      <ActionsMenu :collection="collection" />
    </div>

    <div class="collection-thumbs">
      <div class="image-preview">
        <div
          v-for="(img, index) in visibleImages"
          :key="index"
          class="image-item"
          :class="{ 'half-image': index === 3 }"
        >
          <BaseImage :src="validUrl(img.pages[1].small)" alt="thumb" />
          <div v-if="index === 3 && remainingCount > 0" class="overlay">+{{ remainingCount }}</div>
        </div>
      </div>
    </div>

    <!--    <GenericContentComponentUsage title-key="collections.whatIsInside" class="black-container">-->
    <!--      <TextBody text="No data..." />-->
    <!--    </GenericContentComponentUsage>-->
  </div>
</template>

<script>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import ActionsMenu from '~/components/conversation/collections/ActionsMenu';
import useCollections from '~/composables/useCollections';
import TextBody from '~/components/common/TextBody.vue';
import BaseImage from '~/components/common/BaseImage.vue';
import GenericContentComponentUsage from '~/components/conversation/sidepanelGeneralComponents/GenericContentComponentUsage.vue';
import { calculatePassedTime } from '~/utils/date-utils';
import validateUrl from '~/utils/url';

export default {
  components: { GenericContentComponentUsage, TextBody, ActionsMenu, BaseImage },
  props: {
    collection: {
      type: Object,
      default: () => {},
    },
    thumbs: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { setThumbsCollectionsView, isReadOnly } = useCollections();
    const formatUpdatedAt = computed(
      () => `${t('common.updated')} ${calculatePassedTime(props.collection.modifiedAt)}`,
    );
    const numberOfDocuments = computed(() => `${props.collection.documents.length}  ${t('collections.documents')}`);

    // need this in case we are in collection view breadcrumb, add/delete a document and need to retrieve the current thumbs again when we come back
    watch(
      () => props.collection && props.collection.documents.length,
      async () => {
        await setThumbsCollectionsView([props.collection], 5);
      },
      {},
    );

    const validUrl = (url) => (validateUrl(url) ? url : '');

    const visibleImages = computed(() => props.thumbs.slice(0, 4));

    const remainingCount = computed(() => props.thumbs.length - 3);

    return {
      emit,
      formatUpdatedAt,
      isReadOnly,
      numberOfDocuments,
      validUrl,
      visibleImages,
      remainingCount,
    };
  },
};
</script>

<style scoped lang="scss">
.collection-card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: $bb-neutral-0;
  border-radius: 8px;
  padding: $bb-spacing-3 $bb-spacing-4;
  gap: $bb-spacing-2;

  .collection-header {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .collection-thumbs {
    display: flex;
    justify-content: space-between;
    width: 100%;

    .image-preview {
      display: flex;
      gap: 8px;
      overflow: hidden;
    }

    .image-item {
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      position: relative;
      width: 108px;
      height: 108px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        aspect-ratio: auto;
        object-fit: contain;
        max-width: 80%;
        height: 80%;
        max-height: 80%;
        min-height: 80%;
      }
    }

    .half-image {
      width: 54px;
      overflow: hidden;
    }

    .overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.25);
      color: $bb-neutral-1;
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .collection-title {
    @include bb-medium-text-styles('lg');
  }

  .collection-details {
    @include bb-regular-text-styles('xs');

    span {
      color: $bb-neutral-1;
      letter-spacing: 0.1px;
      font-size: 13px;
      margin-top: $bb-spacing-1;
    }

    .bullet {
      margin: 0 $bb-spacing-1;
    }
  }

  .black-container {
    border-radius: 8px;
    width: 100%;
    padding: $bb-spacing-4;
    color: #fff;
    line-height: 16px;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
  }
}
</style>
